import axios from "axios";
import { toast } from "react-toastify";
import { ENDPOINT_CHECKED_SHARE_IN, ENDPOINT_CREATE_SHARE, ENDPOINT_OTP_REQUEST, ENDPOINT_UPLOAD_CHUNCK, ENDPOINT_OTP_LOGIN } from "./urls";
import { getAxiosErrorMessage, getAxiosErrorMessageAuthenticate } from "../utils/funcErrorMessageCatch";
import { Dispatch, SetStateAction } from "react";
import { getBaseUrl } from "../utils/getBaseUrl";
import { CHUNK_SIZE } from "../utils/constants";
import { AuthResponse, STATES_TYPE, UploadFile } from "../types/type";
import { funcCurrentDateString } from "../utils/funcCurrentDate";
import { TFunction } from "i18next";


export const initWorkspace = async (
        folderName: string,
        access_token: string,
        setWorkspaceData: Dispatch<SetStateAction<any>>,
        t:TFunction
): Promise<string> => {
        try {
                const res = await axios.post(
                        ENDPOINT_CREATE_SHARE,
                        { name: folderName },
                        { headers: { Authorization: `Bearer ${access_token}` } }
                );
                await setWorkspaceData(res.data);
                return res.data?.uid;
        } catch (err) {
                toast.error(`${(t as unknown as (key: string) => string)('key_error_toast_init_share')} ! ${getAxiosErrorMessage(err)}`);
                return "";
        }
};

export const handleCopyShareLink = async (shareLink: string, t:TFunction) => {
        await navigator.clipboard.writeText(shareLink);
        toast.success((t as unknown as (key: string) => string)("key_error_toast_init_clipboard"));
}


export const handleGenerateLink = async (
        workspaceId: string,
        access_token: string | "",
        share_token: string | "",
        setCopied: Dispatch<SetStateAction<boolean>>,
        setShareLink: Dispatch<SetStateAction<string>>,
        setByGetLink: Dispatch<SetStateAction<boolean>>,
        t: TFunction
) => {
        try {
                setCopied(true);
                setByGetLink(true);

                await axios.post(ENDPOINT_CHECKED_SHARE_IN,
                        { share_uid: workspaceId, action_type: "get_link", },
                        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` } }
                );

                setShareLink(`${getBaseUrl()}/share?uid=${workspaceId}&token=${share_token}`)
                setCopied(false);

        } catch (err) {
                toast.error(`${(t as unknown as (key: string) => string)('key_error_toast_generate_link')} ! ${getAxiosErrorMessage(err)}`);
                setCopied(false);
        }
};



export const handleRequestOPT = async (
        e: React.FormEvent,
        email: string,
        setStateUI: Dispatch<SetStateAction<STATES_TYPE>>,
        t: TFunction
) => {
        e.preventDefault();
        try {
                let resp = await axios.post(ENDPOINT_OTP_REQUEST, { email }, { headers: { "Content-Type": "application/json" } });
                if (resp.status === 200) toast.success((t as unknown as (key: string) => string)('key_success_toast_confirmation_mail'))
                setStateUI({ state: "OTP_STATE" });
        } catch (err) {
                toast.error(`${(t as unknown as (key: string) => string)('key_error_toast_request_otp')} ! ${getAxiosErrorMessageAuthenticate(err)}`);
        }
};


export const handleLoginOTP = async (
        e: React.FormEvent,
        email: string,
        otp: string,
        uploadFiles: UploadFile[], 
        setStateUI: Dispatch<SetStateAction<STATES_TYPE>>,
        setDataAuth: Dispatch<SetStateAction<AuthResponse>>,
        setLoadOnly: Dispatch<SetStateAction<boolean>>,
        setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>,
        setShowProgress: Dispatch<SetStateAction<boolean>>,
        setWorkspaceId: Dispatch<SetStateAction<string | "">>,
        setWorkspaceData: Dispatch<SetStateAction<any>>,
        t: TFunction
) => {
        e.preventDefault();
        if (otp.length === 6) {
                try {
                        let resp = await axios.post(ENDPOINT_OTP_LOGIN, { email, otp }, { headers: { "Content-Type": "application/json" } });
                        // if (resp.status === 200) toast.success((t as unknown as (key: string) => string)('key_success_toast_validated_otp'));
                        setDataAuth(resp?.data);
                        setStateUI({ state: "SHARE_STATE" });
                        setLoadOnly(false);

                        // ::::::::::::::::::::::::::::::::::
                        const r = await initWorkspace(`Share-${funcCurrentDateString()}`, resp?.data?.access_token, setWorkspaceData, t)

                        setWorkspaceId(r)
                        
                        for (const file of uploadFiles) {
                          if (file.file && file.status === "uploading") {
                            try {
                              await handleUploadSingleFile(file.file, r, resp?.data, setUploadFiles, setShowProgress, t);
                
                              //update status
                              setUploadFiles(prev =>
                                prev.map(f =>
                                  f.name === file.name ? { ...f, status: "done" } : f
                                )
                              );
                              setShowProgress(false)
                            } catch (err) {
                              setUploadFiles(prev =>
                                prev.map(f =>
                                  f.name === file.name ? { ...f, status: "error" } : f
                                )
                              );
                            }
                          }
                        }
                        // ::::::::::::::::::::::::::::::

                } catch (err) {
                        toast.error(`${(t as unknown as (key: string) => string)('key_error_toast_occurred')} ! ${getAxiosErrorMessageAuthenticate(err)}`);
                }
        } else {
                toast.error((t as unknown as (key: string) => string)('key_error_toast_invalid_otp'));
        }
};


export const handleUploadSingleFile = async (
        file: File, 
        uuid: string, 
        data: AuthResponse,
        setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>,
        setShowProgress: Dispatch<SetStateAction<boolean>>,
        t:TFunction
): Promise<void> => {
        const filename = file.name.length > 100 ? `${file.name.substring(0, 100)}...` : file.name

        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);


        setUploadFiles((prev) => {
                const exists = prev.some(f => f.name === filename);
                if (exists) return prev;
                return [
                        ...prev,
                        {
                                name: filename,
                                size: file.size < 2048 ? `${file.size} Ko` : `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                                loading: 0,
                                status: "uploading",
                                file
                        },
                ];
        });

        setShowProgress(true);

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const start = chunkIndex * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);

                const chunk = file.slice(start, end);

                const formData = new FormData();
                formData.append("file", chunk);
                formData.append("share_uid", uuid);
                formData.append("filename", filename);
                formData.append("chunk_index", chunkIndex.toString());
                formData.append("total_chunks", totalChunks.toString());

                try {
                        await axios.post(ENDPOINT_UPLOAD_CHUNCK, formData,
                                {
                                        headers: {
                                                "Content-Type": "multipart/form-data",
                                                Authorization: `Bearer ${data.access_token}`
                                        },
                                        onUploadProgress: (progressEvent) => {
                                                if (progressEvent.total) {
                                                        // Progress du chunk actuel
                                                        const percentChunk = Math.round(
                                                                (progressEvent.loaded * 100) / progressEvent.total
                                                        );

                                                        // Progress global
                                                        const globalPercent = Math.round(
                                                                ((chunkIndex + percentChunk / 100) / totalChunks) * 100
                                                        );

                                                        setUploadFiles((prev) =>
                                                                prev.map((f) =>
                                                                        f.name === filename ? { ...f, loading: globalPercent } : f
                                                                )
                                                        );
                                                }
                                        },
                                }
                        );
                } catch (err) {
                        toast.error(`${(t as unknown as (key: string) => string)('key_error_toast_upload_chunk')} ! ${getAxiosErrorMessage(err)}`);
                        return;
                }
        }

        setUploadFiles((prev) =>
                prev.map((f) => f.name === filename ? { ...f, loading: 100, status: "done" } : f)
        );
}

